// Staff sign-in: the same WhatsApp code as the apps (Supabase phone OTP; the
// send-otp hook delivers it through Meta, ICD C15). One calm card on the
// mygaz.io glow: the number, then six code boxes that fill as you type or
// paste, over a living dot field (EntryBackdrop). One code keeps the computer
// signed in for 30 days (lib/session); the page no longer says so.
// Only a MyGaz admin gets past ops_me; anyone else sees why.
import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { S } from '../lib/strings';
import { cmPhone, prettyPhone, typedPhone } from '../lib/phone';
import { markSignedIn, SIGNED_IN_DAYS } from '../lib/session';
import { Ic } from '../components/Icon';
import { LangSwitch } from '../components/Lang';
import { Wordmark } from '../components/Wordmark';
import { EntryBackdrop } from '../components/EntryBackdrop';

const RESEND_S = 30;

function WhatsApp() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8s-.4-.1-.6.1-.7.8-.8 1-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.2-.4.2-.4.7-1.3.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4c1.7.7 2.3.8 3.2.6a2.7 2.7 0 0 0 1.8-1.2 2.2 2.2 0 0 0 .2-1.2c-.1-.1-.3-.2-.6-.3z" />
    </svg>
  );
}

function CodeBoxes({ value, onChange, disabled }: { value: string; onChange(v: string): void; disabled: boolean }) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => { refs.current[0]?.focus(); }, []);
  const set = (i: number, raw: string) => {
    const d = raw.replace(/\D/g, '');
    if (!d) return;
    const next = (value.slice(0, i) + d + value.slice(i + d.length)).slice(0, 6);
    onChange(next);
    refs.current[Math.min(5, i + d.length)]?.focus();
  };
  return (
    <div className="otp" role="group" aria-label={S.login.code}>
      {Array.from({ length: 6 }, (_, i) => (
        <input key={i} ref={(el) => { refs.current[i] = el; }} className={value[i] ? 'on' : ''} inputMode="numeric" autoComplete={i ? 'off' : 'one-time-code'}
          aria-label={`${S.login.code} ${i + 1}`} value={value[i] ?? ''} disabled={disabled} maxLength={6}
          onChange={(e) => set(i, e.target.value)}
          onPaste={(e) => { e.preventDefault(); set(0, e.clipboardData.getData('text')); }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace') { e.preventDefault(); const at = value[i] ? i : Math.max(0, i - 1); onChange(value.slice(0, at)); refs.current[at]?.focus(); }
            if (e.key === 'ArrowLeft') refs.current[Math.max(0, i - 1)]?.focus();
            if (e.key === 'ArrowRight') refs.current[Math.min(5, i + 1)]?.focus();
          }} />
      ))}
    </div>
  );
}

export function Login({ onDemo, notAdmin, expired, onSignOut }: { onDemo?: () => void; notAdmin: boolean; expired: boolean; onSignOut(): void }) {
  const [phone, setPhone] = useState('');
  const [sent, setSent] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) return;
    const id = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(id);
  }, [wait]);

  async function sendTo(p: string) {
    if (!supabase) return;
    setBusy(true); setErr(null);
    const { error } = await supabase.auth.signInWithOtp({ phone: p, options: { shouldCreateUser: false } });
    setBusy(false);
    if (error) setErr(S.login.failed); else { setSent(p); setWait(RESEND_S); }
  }
  async function send(e: React.FormEvent) {
    e.preventDefault();
    const p = cmPhone(phone);
    if (!p) { setErr(S.login.badPhone); return; }
    await sendTo(p);
  }
  async function verify(token: string) {
    if (!supabase || !sent) return;
    setBusy(true); setErr(null);
    markSignedIn(); // before verifyOtp: its SIGNED_IN event is checked against this date
    const { error } = await supabase.auth.verifyOtp({ phone: sent, token, type: 'sms' });
    setBusy(false);
    if (error) { setErr(S.login.badCode); setCode(''); } else setOk(true);
  }
  const onCode = (v: string) => { setCode(v); setErr(null); if (v.length === 6) void verify(v); };
  const restart = () => { setSent(null); setCode(''); setErr(null); };
  const digits = phone.replace(/\D/g, '').length;

  return (
    <main className="entry">
      <EntryBackdrop />
      <header className="entry-top">
        <div className="entry-brand"><img src="./mygaz-icon.png" alt="" /><Wordmark /><span className="tag">Ops</span></div>
        <LangSwitch />
      </header>
      <section className="entry-center">
        <div className="entry-card">
          <div className="entry-mark" aria-hidden="true">
            <i /><i />
            {ok ? <span className="entry-ok"><Ic n="check" /></span> : <img src="./mygaz-icon.png" alt="" />}
          </div>
          {notAdmin ? (
            <div className="entry-step" key="na">
              <span className="entry-eyebrow r">{S.login.eyebrow}</span>
              <h1>{S.login.notAdmin}</h1><p>{S.login.notAdminSub}</p>
              <button className="btn go ghost" onClick={onSignOut}>{S.login.signOut}</button>
            </div>
          ) : !supabase ? (
            <div className="entry-step" key="nb">
              <span className="entry-eyebrow">{S.login.eyebrow}</span>
              <h1>{S.login.title}</h1><p>{S.login.noBackend}</p>
              {onDemo && <button className="btn go" onClick={onDemo}>{S.login.demo}</button>}
            </div>
          ) : !sent ? (
            <form className="entry-step" key="p" onSubmit={send}>
              <span className="entry-eyebrow">{S.login.eyebrow}</span>
              <h1>{S.login.headA}<em>{S.login.headB}</em></h1>
              <p>{S.login.sub}</p>
              {expired && <div className="entry-note" role="status"><Ic n="clock" s="xs" /><span>{S.login.expired(SIGNED_IN_DAYS)}</span></div>}
              <label className="fld entry-fld"><span>{S.login.phone}</span>
                <div className="phone-in"><span className="cc">+237</span>
                  <input inputMode="tel" autoComplete="tel-national" placeholder={S.login.phoneHint} value={phone}
                    onChange={(e) => { setPhone(typedPhone(e.target.value)); setErr(null); }} autoFocus /></div>
              </label>
              {err && <p className="login-err" role="alert">{err}</p>}
              <button className="btn go" disabled={busy || digits < 9}>{busy ? <span className="spin" /> : <WhatsApp />}{S.login.sendCode}</button>
            </form>
          ) : (
            <div className="entry-step" key="c">
              <span className="entry-eyebrow">{ok ? S.login.welcome : S.login.codeEyebrow}</span>
              <h1>{ok ? S.login.opening : S.login.codeTitle}</h1>
              {!ok && <>
                <p>{S.login.sentTo(prettyPhone(sent))}{' '}<button type="button" className="link" onClick={restart}>{S.login.change}</button></p>
                <CodeBoxes value={code} onChange={onCode} disabled={busy} />
                {err && <p className="login-err" role="alert">{err}</p>}
                <div className="entry-resend">
                  {wait > 0 ? <span className="num">{S.login.resendIn(wait)}</span>
                    : <button type="button" className="link" disabled={busy} onClick={() => void sendTo(sent)}>{S.login.resend}</button>}
                </div>
              </>}
            </div>
          )}
        </div>
        {onDemo && supabase && !notAdmin && !sent && <button type="button" className="link entry-demo" onClick={onDemo}>{S.login.demo}</button>}
      </section>
      <footer className="entry-foot"><Ic n="lock" s="xs" />{S.login.foot}</footer>
    </main>
  );
}
